<template>
  <div v-if="entries && entries.length">
    <section
      v-for="(slice, index) in entries"
      :id="slice.sys.id.toString()"
      :key="'entry-slice_' + slice.sys.id.toString()"
      :ref="'slice-ref_' + slice.sys.id.toString()"
    >
      <!--  ==============================================================================-->
      <!--  Full Width Slices -->
      <!--  ==============================================================================-->
      <template>
        <small-banner
          v-if="slice.sys.contentType.sys.id.toString() === 'buttonBanner'"
          :fields="slice.fields"
          :assets="assets"
          :slice-refs="{ refs: $refs, index }"
        />
        <enrollment-banner
          v-if="slice.sys.contentType.sys.id.toString() === 'enrollmentBanner'"
          :fields="slice.fields"
        />
        <contact-banner
          v-if="slice.sys.contentType.sys.id.toString() === 'contactBanner'"
          :fields="slice.fields"
        />
        <standalone-video
          v-if="
            slice.sys.contentType.sys.id.toString() === 'standaloneVideoBlock'
          "
          :fields="slice.fields"
          :assets="assets"
        />
        <quiz-slice
          v-if="slice.sys.contentType.sys.id.toString() === 'quiz'"
          :sys-id="slice.sys.id"
        />
        <content-banner
          v-if="slice.sys.contentType.sys.id.toString() === 'contentBanner'"
          :fields="slice.fields"
          :assets="assets"
        />
        <plan-comparison-group
          v-if="
            slice.sys.contentType.sys.id.toString() === 'planComparisonGroup'
          "
          :fields="slice.fields"
          :sys-id="slice.sys.id"
        />
        <age-in-graph
          v-if="
            slice.sys.contentType.sys.id.toString() === 'ageInGraph'
            "
            :fields="slice.fields"
            :sys-id="slice.sys.id"
          />
      </template>
      <!--  ==============================================================================-->
      <!--  Handles Constraint Within Component Slices -->
      <!--  ==============================================================================-->
      <template>
        <standalone-text
          v-if="
            slice.sys.contentType.sys.id.toString() === 'standaloneTextBlock'
          "
          :fields="slice.fields"
          :slice-refs="{ refs: $refs, index }"
          :enrollment-banner-is-below="nextSliceIsEnrollmentBanner(index)"
          :enrollment-banner-is-above="previousSliceIsEnrollmentBanner(index)"
          :assets="assets"
        />
        <value-proposition
          v-if="slice.sys.contentType.sys.id.toString() === 'valuePropositions'"
          :fields="slice.fields"
          :assets="assets"
        />
      </template>
      <!--  ==============================================================================-->
      <!--  Constrained Centered Slices -->
      <!--  ==============================================================================-->
      <div class="content-center">
        <div class="content-constraint">
          <template>
            <text-tiles
              v-if="slice.sys.contentType.sys.id.toString() === 'textTileGroup'"
              :sys-id="slice.sys.id.toString()"
            />
            <event-group
              v-if="slice.sys.contentType.sys.id.toString() === 'eventGroup'"
              :fields="slice.fields"
              :sys-id="slice.sys.id.toString()"
            />
            <expansion-panel-faq
              v-if="slice.sys.contentType.sys.id.toString() === 'faq'"
              :fields="slice.fields"
              :sys-id="slice.sys.id.toString()"
            />
            <template
              v-if="slice.sys.contentType.sys.id.toString() === 'contactUsForm'"
            >
              <!-- Fraud form is quite different than the others, so it has been split into it's own component -->
              <fraud-form v-if="slice.fields.type === 'Report Fraud'" :fields="slice.fields" />
              <contact-form v-else :fields="slice.fields" />
            </template>
            <rsvp-form
              v-if="slice.sys.contentType.sys.id.toString() === 'rsvpForm'"
              :fields="slice.fields"
              :sys-id="slice.sys.id">
            </rsvp-form>
            <rich-text
              v-if="slice.sys.contentType.sys.id.toString() === 'richText'"
              :fields="slice.fields"
            />
            <icon-tile-group
              v-if="slice.sys.contentType.sys.id.toString() === 'iconTileGroup'"
              :sys-id="slice.sys.id.toString()"
              :fields="slice.fields"
            />
            <table-grid
              v-if="slice.sys.contentType.sys.id.toString() === 'tableGrid'"
            :fields="slice.fields"
            :sys-id="slice.sys.id"
          />
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ValueProposition from '@/components/slices/content/value-proposition'
import StandaloneText from '@/components/slices/content/standalone-text'
import TextTiles from '@/components/slices/text-tiles/text-tiles'
import EventGroup from '@/components/slices/events/event-item-group'
import StandaloneVideo from '@/components/slices/content/standalone-video'
import ExpansionPanelFaq from '@/components/slices/content/expansion-panel-faq'
import EnrollmentBanner from '@/components/slices/banners/enrollment-banner'
import ContactBanner from '@/components/slices/banners/contact-banner'
import ContactForm from '@/components/slices/forms/contact-form'
import RichText from '@/components/slices/content/rich-text'
import QuizSlice from '@/components/slices/quiz/quiz-slice'
import ContentBanner from '@/components/slices/banners/content-banner'
import SmallBanner from '@/components/slices/banners/small-banner'
import PlanComparisonGroup from '@/components/slices/plan-comparison/plan-comparison-group'
import IconTileGroup from '@/components/slices/icon-tiles/icon-tile-group.vue'
import FraudForm from './forms/fraud-form.vue'
import AgeInGraph from './agein/age-in-graph.vue'
import TableGrid from './tablegrid/tablegrid.vue'
import RsvpForm from './forms/rsvp-form.vue'

export default {
  name: 'SlicesBlock',
  components: {
    ContentBanner,
    SmallBanner,
    QuizSlice,
    RichText,
    ContactBanner,
    ExpansionPanelFaq,
    StandaloneVideo,
    ValueProposition,
    StandaloneText,
    TextTiles,
    EventGroup,
    EnrollmentBanner,
    ContactForm,
    PlanComparisonGroup,
    IconTileGroup,
    FraudForm,
    AgeInGraph,
    TableGrid,
    RsvpForm
  },
  props: {
    assets: {
      type: Array,
      default: null
    },
    entries: {
      type: Array,
      default: null
    }
  },
  methods: {
    nextSliceIsEnrollmentBanner (index) {
      return (
        this.entries[index + 1] &&
        this.entries[index + 1].sys.contentType.sys.id.toString() ===
          'enrollmentBanner'
      )
    },
    previousSliceIsEnrollmentBanner (index) {
      return (
        this.entries[index - 1] &&
        this.entries[index - 1].sys.contentType.sys.id.toString() ===
          'enrollmentBanner'
      )
    }
  }
}
</script>
